import {
	Grid2,
	IconButton,
	Paper,
	Skeleton,
	styled,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from '@mui/material';
import { useMainCustomStyle } from '../assets/theme/Theme';
import SideNavigationBar from '../components/SideNavigationBar';
import { useEffect, useState } from 'react';
import {
	AgentRequestList,
	AgentRequestListResponse,
	ChatData,
	Chats,
	ChatsResponse,
} from '../types';
import { fetchAgentRequestsList, fetchChats } from '../services/ApiService';
import _ from 'lodash';
import { convertTo12HourTime, dateFormatter } from '../Utils/Helpers';
import { EyeIcon } from '../assets/icons/Icons';
import ChatPopup from '../components/ChatPopup';

function AgentRequests() {
	const mainDivStyle = useMainCustomStyle();
	const [requestList, setRequestList] = useState<Array<AgentRequestList>>([]);
	const [rowsPerPage, setRowsPerPage] = useState(8);
	const [page, setPage] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [popupTitle, setPopupTitle] = useState('');
	const [selectedChatData, setSelectedChatData] = useState<{
		[index: string]: Array<ChatData>;
	}>({});
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		getAllAgentRequests();
	}, []);

	async function getAllAgentRequests() {
		setIsLoading(true);
		const data: Array<AgentRequestListResponse> =
			await fetchAgentRequestsList();
		const orderByTime = _.orderBy(
			data,
			'Last_message_retrieve_time',
			'desc',
		);

		const sortedData: Array<AgentRequestList> = orderByTime.map(item => {
			const convertTime = convertTo12HourTime(
				item.Last_message_retrieve_time.split(' ')[1],
			);
			const value: AgentRequestList = {
				messageDate: dateFormatter(
					item.Last_message_retrieve_time.split(' ')[0],
				),
				messageTime: convertTime,
				clientNumber: item.Phone_num,
				sessionDateTime: item.Last_message_retrieve_time,
				clientName:
					item.Name !== 'Unknown' && item.Name !== ''
						? item.Name
						: 'N/A',
			};

			return value;
		});

		setRequestList(sortedData);
		setIsLoading(false);
	}

	const handleChangePage = (
		event: React.MouseEvent<HTMLButtonElement> | null,
		newPage: number,
	) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	async function onViewChats(
		phone: string,
		name: string,
		sessionEndDateTime: string,
	) {
		if (name && name !== 'N/A') {
			setPopupTitle(`${name} ${phone}`);
		} else {
			setPopupTitle(phone);
		}

		const data: Array<ChatsResponse> = await fetchChats(
			phone.split(' ')[0],
		);

		const updatedData: Array<Chats> = data.map(item => {
			return {
				message: item.message,
				name: phone,
				timestamp: item.time_stamp,
				type: item.type,
				status: item.seen_status,
			};
		});

		const filteredData: Array<Chats> = _.filter(
			updatedData,
			item =>
				new Date(item.timestamp).getTime() <=
				new Date(sessionEndDateTime).getTime(),
		);

		const orderByTime = _.orderBy(filteredData, 'timestamp');

		const chatMessages: Array<ChatData> = orderByTime.map(item => {
			return {
				type: item.type,
				text: item.message,
				time: convertTo12HourTime(item.timestamp.split(' ')[1]),
				date: item.timestamp.split(' ')[0],
				status: item.status,
			};
		});

		const groupByDate = _.groupBy(chatMessages, 'date');

		// Order Object By Date
		const entries = _.toPairs(groupByDate);
		const sortedEntries = _.orderBy(
			entries,
			[entry => new Date(entry[0])],
			['asc'],
		);
		const sortedObj = _.fromPairs(sortedEntries);

		setSelectedChatData(sortedObj);
		setIsModalOpen(true);
	}

	function onCloseModal() {
		setIsModalOpen(false);
		setSelectedChatData({});
		setPopupTitle('');
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid2 container paddingLeft={'20px'} paddingRight={'20px'}>
					<Grid2
						container
						sx={{ paddingLeft: '10px', width: '100%' }}>
						<TableContainer
							component={Paper}
							style={{
								boxShadow: 'none',
							}}>
							<Table
								sx={{ minWidth: 700, width: '100%' }}
								aria-label="customized table">
								<TableHead>
									<TableRow>
										<StyledTableCell align="center">
											<TableHeaderText>
												Client Number
											</TableHeaderText>
										</StyledTableCell>
										<StyledTableCell align="center">
											<TableHeaderText>
												Client Name
											</TableHeaderText>
										</StyledTableCell>
										<StyledTableCell align="center">
											<TableHeaderText>
												Date
											</TableHeaderText>
										</StyledTableCell>
										<StyledTableCell align="center">
											<TableHeaderText>
												Time
											</TableHeaderText>
										</StyledTableCell>
										<StyledTableCell align="center">
											<TableHeaderText>
												View Chat
											</TableHeaderText>
										</StyledTableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{isLoading ? (
										<StyledTableRow>
											{[...Array(5)].map((_, index) => (
												<StyledTableCell
													key={index}
													align="center">
													<Skeleton
														animation="wave"
														style={{
															borderRadius: '5px',
														}}
														variant="rounded"
													/>
												</StyledTableCell>
											))}
										</StyledTableRow>
									) : requestList &&
									  requestList.length > 0 ? (
										requestList
											.slice(
												page * rowsPerPage,
												page * rowsPerPage +
													rowsPerPage,
											)
											.map((chat, index) => {
												return (
													<StyledTableRow key={index}>
														<StyledTableCell align="center">
															<TableRowText>
																{
																	chat.clientNumber.split(
																		' ',
																	)[0]
																}
															</TableRowText>
														</StyledTableCell>
														<StyledTableCell align="center">
															<TableRowText>
																{
																	chat.clientName
																}
															</TableRowText>
														</StyledTableCell>
														<StyledTableCell align="center">
															<TableRowText>
																{
																	chat.messageDate
																}
															</TableRowText>
														</StyledTableCell>
														<StyledTableCell align="center">
															<TableRowText>
																{
																	chat.messageTime
																}
															</TableRowText>
														</StyledTableCell>
														<StyledTableCell align="center">
															<IconButton
																sx={{
																	padding:
																		'0px',
																}}
																onClick={() =>
																	onViewChats(
																		chat.clientNumber,
																		chat.clientName,
																		`${chat.clientNumber.split(' ')[1]} ${chat.clientNumber.split(' ')[2]}`,
																	)
																}>
																<EyeIcon />
															</IconButton>
														</StyledTableCell>
													</StyledTableRow>
												);
											})
									) : (
										<StyledTableRow>
											<StyledTableCell
												colSpan={7}
												align="center">
												<TableRowText>
													No data available
												</TableRowText>
											</StyledTableCell>
										</StyledTableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							rowsPerPageOptions={[]}
							component="div"
							count={requestList && requestList.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Grid2>
				</Grid2>
			</div>
			<ChatPopup
				data={selectedChatData}
				isModalOpen={isModalOpen}
				popupTitle={popupTitle}
				onClose={onCloseModal}
				isChatView={
					selectedChatData && Object.keys(selectedChatData).length > 0
				}
			/>
		</>
	);
}

export const StyledTableCell = styled(TableCell)(() => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: '#FFFFFF',
		color: 'rgba(0, 0, 0, 0.5)',
		paddingTop: '20px',
		fontSize: 12,
		fontWeight: 600,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 12,
		fontWeight: 400,
		color: '#000000',
		maxWidth: 80,
	},
}));

export const StyledTableRow = styled(TableRow)(() => ({
	'&:nth-of-type(odd)': {
		background:
			'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #C2C9D1',
	},
	// hide last border
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

export const TableHeaderText = styled(Typography)(() => ({
	fontSize: '12px',
	fontWeight: '600',
	color: 'rgba(0, 0, 0, 0.5)',
}));

export const TableRowText = styled(Typography)(() => ({
	fontSize: '12px',
	fontWeight: '400',
	color: 'rgba(0, 0, 0, 1)',
}));

export default AgentRequests;
